import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { io, Socket as SocketIOClient } from 'socket.io-client';
import { environment } from "../../../../environments/environment";
import { ChatMessage, Message, MessageOrderData } from "./interfaces/message.interface";
import {
  SocketJoinedChatResponse,
  SocketChatErrorResponse,
  SocketChatHistoryResponse,
  SocketUserReconnectedResponse
} from '../../../shared/interfaces/chat-reconnection.interface';

@Injectable({
  providedIn: 'root'
})
export class SocketService {
  private socket: SocketIOClient;
  private connected = false;

  constructor() {
    // console.log('Iniciando SocketService - Conectando a:', environment.socketUrl);
    this.socket = io(environment.socketUrl, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      timeout: 20000,
      forceNew: true
    });

    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    this.socket.on('connect', () => {
      this.connected = true;
      // console.log('Socket conectado en socket Delivery:', this.socket.id);
    });

    this.socket.on('disconnect', (reason) => {
      this.connected = false;
      // console.log('Socket desconectado. Razón:', reason);
    });

    this.socket.on('connect_error', (error) => {
      console.error('Error de conexión del socket:', error);
    });

    this.socket.on('reconnect', (attemptNumber) => {
      // console.log('Socket reconectado después de', attemptNumber, 'intentos');
    });

    this.socket.on('reconnect_error', (error) => {
      console.error('Error al intentar reconectar:', error);
    });
  }

  emitNewOrder(orderId: number, establishmentId: number): Promise<void> {
    // console.log('Intentando emitir nueva orden:', { orderId, establishmentId });
    // console.log('Estado actual del socket - Conectado:', this.socket.connected, 'ID:', this.socket.id);

    return new Promise((resolve, reject) => {
      const orderData = {
        orderId: orderId.toString(),
        establishmentId: establishmentId.toString()
      };

      const emitOrder = () => {
        try {
          // console.log('Emitiendo orden con datos:', orderData);
          this.socket.emit('orderDelivery', orderData);
          // console.log('Orden emitida exitosamente:', { orderId, establishmentId });
          resolve();
        } catch (error) {
          console.error('Error al emitir orden:', error);
          reject(error);
        }
      };

      if (!this.socket.connected) {
        // console.log('Socket no conectado, iniciando reconexión...');
        this.socket.once('connect', () => {
          // console.log('Socket reconectado, emitiendo orden...');
          emitOrder();
        });
        this.socket.connect();
      } else {
        emitOrder();
      }
    });
  }

  onNewOrder(callback: (data: any) => void): void {
    // console.log('Registering listener for new orders. Socket connected:', this.socket.connected);
    // console.log('Socket ID:', this.socket.id);

    // Remover listeners anteriores para evitar duplicados
    this.socket.off('orderDelivery');

    this.socket.on('orderDelivery', (data: any) => {
      // console.log('New order received - raw data:', data);
      // console.log('Socket ID when receiving:', this.socket.id);

      try {
        // Handle array format
        let processedData = data;
        if (Array.isArray(data)) {
          processedData = data[0];  // Take first element which contains the response object
        }

        // Ensure we have an object with required properties
        if (processedData && typeof processedData === 'object') {
          // Normalize the response to ensure it's either 1 or 0
          if ('response' in processedData) {
            processedData.response = processedData.response === 1 ? 1 : 0;
          } else {
            processedData.response = 0;  // Default to 0 if no response property
          }

          // console.log('Processed order data:', processedData);
          callback(processedData);
        } else {
          console.warn('Unexpected data format:', data);
          // Send a normalized rejection response
          callback({ orderId: data.orderId, response: 0 });
        }
      } catch (error) {
        console.error('Error processing order data:', error);
        // Send a normalized rejection response
        callback({ orderId: data.orderId, response: 0 });
      }
    });
  }
  sendChatMessage(
    message: string,
    username: string,
    time: string = '',
    cht: string,
    type: string = 'call-center',
    origin: string = 'client'
  ): void {
    const currentTime = new Date().toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    });

    // Objeto estructurado
    const messageData = {
      message: message,
      username: username,
      time: time || currentTime,
      cht: cht,
      type: type,
      origin: origin
    };

    // Emite como objeto único
    this.socket.emit('new message', messageData);

    // console.log('Emitting message as object:', messageData);
  }
  sendMessageOrder(
    message: string,
    username: string,
    time: string,
    chat: string,
    origin: string | null = null,
    content: string = 'txt'
  ): void {
    const data: MessageOrderData = {
      chat,
      message,
      user: username,
      time_at: time,
      content,
      origin
    };

    // console.log('Sending message order:', data);
    this.socket.emit('message orders', message, username, time, chat, origin, content);
  }
  // sendChatOrder(message: string, username: string): void {
  //   // console.log(`Sending chat order: ${message} by ${username}`);
  //   this.socket.emit('chat orders', message, username);
  // }
  // onChatOrder(callback: (message: ChatMessage) => void): void {
  //   this.socket.on('chat orders', (msg: string, user: string) => {
  //     // console.log(`New chat order received: ${msg} by ${user}`);
  //     callback({
  //       message: msg,
  //       username: user,
  //       serverOffset: 0,
  //       time: '',
  //       cht: '',
  //       type: '',
  //       origin: ''
  //     });
  //   });
  // }
  onMessageOrder(callback: (data: MessageOrderData) => void): void {
    this.socket.on('message orders', (
      msg: string,
      user: string,
      time: string,
      cht: string,
      origin: string | null = null,
      content: string = 'txt'
    ) => {
      const messageData: MessageOrderData = {
        chat: cht,
        message: msg,
        user,
        time_at: time,
        content,
        origin
      };

      // console.log('Message order received:', messageData);
      callback(messageData);
    });
  }
  onChatMessage(callback: (message: ChatMessage) => void): void {
    this.socket.off('new message'); // Remueve listeners anteriores
    this.socket.on('new message', (data: any) => {
      // Asegúrate de que data es un objeto
      const isImageMessage = data.imageUrl || data.content === 'img';
      const messageData: ChatMessage = {
        message: data.message || undefined, // Usar data.message directamente
        username: data.username || 'Anónimo',

        time: data.time || new Date().toLocaleTimeString(),
        cht: data.cht || '',
        type: data.type || '',
        origin: data.origin || 'client',
        content: isImageMessage ? 'img' : 'txt',
        fileUrl: data.imageUrl || data.fileUrl,
        fileName: data.fileName,
        fileSize: data.fileSize,
        mimeType: data.mimeType
      };

      // if (this.currentChatId && messageData.cht === this.currentChatId) {
      //   callback(messageData);
      // }
    });
  }
  // setCurrentChatId(chatId: string): void {
  //   this.currentChatId = chatId;
  // }

  emit(event: string, data: any): Promise<void> {
    // console.log(`Intentando emitir evento ${event}:`, data);

    return new Promise((resolve, reject) => {
      if (!this.socket.connected) {
        // console.log('Socket no conectado, iniciando reconexión...');
        this.socket.connect();
      }

      try {
        this.socket.emit(event, data);
        // console.log(`Evento ${event} emitido exitosamente:`, data);
        resolve();
      } catch (error) {
        console.error(`Error al emitir evento ${event}:`, error);
        reject(error);
      }
    });
  }

  onOrderDeliveryResponse(callback: (data: { orderId: string, response: number }) => void): void {
    // console.log('Registrando listener para orderDeliveryResponse');

    this.socket.on('orderDeliveryResponse', (data: any) => {
      // console.log('orderDeliveryResponse recibido - datos raw:', data);

      try {
        const parsedData = typeof data === 'string' ? JSON.parse(data) : data;
        // console.log('orderDeliveryResponse datos parseados:', parsedData);

        // Verificación de estructura de datos
        if (parsedData && ('orderId' in parsedData) && ('response' in parsedData)) {
          // console.log('Estructura de datos válida, ejecutando callback');
          callback(parsedData);
        } else {
          console.warn('Estructura de datos inválida:', parsedData);
          callback(data); // Fallback al dato original
        }
      } catch (error) {
        console.error('Error al parsear orderDeliveryResponse:', error);
        callback(data);
      }
    });
  }

  on(event: string, callback: (data: any) => void): void {
    // console.log(`Registrando listener para evento: ${event}`);

    this.socket.on(event, (data: any) => {
      // console.log(`Datos recibidos para evento ${event}:`, data);

      try {
        const parsedData = typeof data === 'string' ? JSON.parse(data) : data;
        // console.log(`Datos parseados para evento ${event}:`, parsedData);
        callback(parsedData);
      } catch (error) {
        //console.error(`Error al parsear datos del evento ${event}:`, error);
        callback(data);
      }
    });
  }
  onE(event: string): Observable<any> {
    return new Observable(observer => {
      this.socket.on(event, (data) => observer.next(data));
      return () => this.socket.off(event);
    });
  }
  joinChat(chatId: string): void {
    // console.log('Uniendose a la sala');
    this.socket.emit(`chat_${chatId}`);
  } 
  disconnect(): void {
    // console.log('Iniciando desconexión del socket');
    if (this.socket) {
      this.socket.disconnect();
      // console.log('Socket desconectado exitosamente');
    }
  }

  isSocketConnected(): boolean {
    const connected = this.socket.connected;
    // console.log('Estado actual de conexión:', connected);
    return connected;
  }

  // getConnectionStatus(): Observable<boolean> {
  //   return this.isConnected.asObservable();
  // }

  connect(): void {
    if (!this.socket.connected) {
      // console.log('Iniciando conexión del socket');
      this.socket.connect();
    } else {
      // console.log('Socket ya está conectado');
    }
  }

  removeListener(event: string): void {
    // console.log(`Removiendo listener para evento: ${event}`);
    this.socket.off(event);
  }

  removeAllListeners(event?: string): void {
    if (event) {
      // console.log(`Removiendo todos los listeners para evento: ${event}`);
      this.socket.removeAllListeners(event);
    } else {
      // console.log('Removiendo todos los listeners');
      this.socket.removeAllListeners();
    }
  }

  getSocketId(): string | null {
    const socketId = this.socket?.id || null;
    // console.log('ID actual del socket:', socketId);
    return socketId;
  }

  joinRoomSoporte(UserId: number, username: string): void {




    const payload = {
      room: 'soporte',
      UserId,
      username
    }

      // console.log('obejto',payload);
    this.socket.emit('join-room', payload);

  }
  sendMessageSupport(message: Message): void {
    // console.log('mensaje de cliente mkp', message);

    this.socket.emit('messageSup', message);
  }

  // Método para unirse a una sala de chat específica
  joinChatRoom(chatId: string, userId: number, username: string): void {
    const payload = {
      chatId,
      userId,
      username
    };

    // console.log('Uniéndose a la sala de chat:', payload);
    this.socket.emit('join-chat', payload);
  }

  // Método para obtener el historial de un chat
  getChatHistory(chatId: string): void {
    // console.log('Solicitando historial del chat:', chatId);
    this.socket.emit('get-chat-history', { chatId });
  }

  // Método para reconectar automáticamente a un chat activo
  reconnectToActiveChat(chatId: string, userId: number, username: string): void {
    // console.log('Reconectando a chat activo:', { chatId, userId, username });

    // Primero unirse a la sala
    this.joinChatRoom(chatId, userId, username);

    // Luego solicitar el historial
    setTimeout(() => {
      this.getChatHistory(chatId);
    }, 500); // Pequeño delay para asegurar que se unió a la sala
  }

  // Observables para los nuevos eventos
  onJoinedChat(): Observable<SocketJoinedChatResponse> {
    return this.onE('joined-chat');
  }

  onJoinChatError(): Observable<SocketChatErrorResponse> {
    return this.onE('join-chat-error');
  }

  onChatHistory(): Observable<SocketChatHistoryResponse> {
    return this.onE('chat-history');
  }

  onChatHistoryError(): Observable<SocketChatErrorResponse> {
    return this.onE('chat-history-error');
  }

  onUserReconnected(): Observable<SocketUserReconnectedResponse> {
    return this.onE('user-reconnected');
  }

}
